/* eslint-disable max-lines */

import { NgTemplateOutlet, TitleCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { RouterLink } from '@angular/router';

import { Store } from '@ngrx/store';
import moment from 'moment-timezone';
import { MomentModule } from 'ngx-moment';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import type { User } from '@recruitee/account-types';
import { OverdueCandidateBadgeComponent } from '@recruitee/candidate-ui';
import { Helpers, NgTemplateAssertType, TimeService, trackByValue } from '@recruitee/common';
import { DevFlag, injectDevFlag } from '@recruitee/dev-flags-data';
import { EEODataStatus } from '@recruitee/eeo-types';
import type { TGdprStatus } from '@recruitee/gdpr-types';
import { GdprStatus } from '@recruitee/gdpr-types';
import { I18nService, TranslatePipe } from '@recruitee/i18n';
import { getElementSignature, IntegrationsElementOutletComponent } from '@recruitee/integrations';
import { IntegrationsElement } from '@recruitee/integrations-types';
import { LocationSelectors } from '@recruitee/locations-data';
import type { LocationBrief } from '@recruitee/locations-types';
import { AppNavigatorService } from '@recruitee/navigation';
import type { TOfferKind } from '@recruitee/offers-types';
import { OfferKind } from '@recruitee/offers-types';
import type { CandidatePlacement, FullSearchCandidateRecord } from '@recruitee/search-types';
import { DisqualifyKind } from '@recruitee/search-types';
import { HiddenScoreIconComponent } from '@recruitee/unbiased-hiring';
import {
  AvatarComponent,
  BadgeComponent,
  ButtonComponent,
  DataTableCellContext,
  DataTableColumnGeometryChange,
  DataTableComponent,
  DataTableRow,
  DateComponent,
  DaysLeftPipe,
  DividerComponent,
  IconColorDirective,
  IconComponent,
  InterviewEvaluationRatingComponent,
  OfferStatusIndicatorComponent,
  PhoneNumberPipe,
  PlaceloaderComponent,
  RtTooltip,
  TagComponent,
  TruncateComponent,
} from '@recruitee/user-interface';
import { getTextContentFromHTML, LifeCycle } from '@recruitee/utils';

import {
  CandidateColumnId,
  CandidateDataTableColumn,
  OfferAccessConfig,
  SortByType,
  ViewType,
} from './candidates-table.types';

interface CandidateTableOffer {
  id: number;
  title: string;
  kind: TOfferKind;
  status: string;
}

interface CandidatesTableStage {
  name: string;
  disqualified: boolean;
  overdueDiff: number | null;
  hired?: boolean;
}

export const ExpandableCellGroups: Record<string, string[]> = {
  placements: [
    CandidateColumnId.Jobs,
    CandidateColumnId.Stages,
    CandidateColumnId.EEOStatus,
    CandidateColumnId.HiredAt,
    CandidateColumnId.JobStartsAt,
    CandidateColumnId.WorkLocation,
    CandidateColumnId.JobLocationName,
    CandidateColumnId.JobLocationAddress,
    CandidateColumnId.JobFitScore,
    CandidateColumnId.DisqualifiedBy,
  ],
};

export type CandidateDataTableRowLimits = Record<keyof typeof ExpandableCellGroups, number>;

export interface CandidatesTableRow extends FullSearchCandidateRecord {
  stages: CandidatesTableStage[];
  jobPlacements: CandidatePlacement[];
  jobs: CandidateTableOffer[];
  talentPools: CandidateTableOffer[];
  workLocations: LocationBrief[][];
  jobLocations: LocationBrief[][];
  allWorkLocations: LocationBrief[][];
  allJobLocations: LocationBrief[][];
  hiddenWorkLocationsLength: number;
  hiddenJobLocationsLength: number;
  locationPlaceholders: LocationBrief[][];
  allJobPlacements: CandidatePlacement[];
  allStages: CandidatesTableStage[];
  allJobs: CandidateTableOffer[];
  allTalentPools: CandidateTableOffer[];
  formattedHighlights: CandidatesTableRowHighlight[];
  deletedByAdmin: User;
  deletedByName?: string | undefined;
  deletedByGDPR: boolean;
}

export interface CandidatesTableRowHighlight {
  key: string;
  label: string;
  allMatches: string[];
  match: string;
  additionalMatches: string[];
  detailsInTooltip: boolean;
}

type ViewTypeStr = `${ViewType}`;

@Component({
  selector: 'rt-search-page-candidates-table',
  templateUrl: './candidates-table.component.html',
  styleUrls: ['./candidates-table.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NgTemplateOutlet,
    ButtonComponent,
    TruncateComponent,
    NgTemplateAssertType,
    AvatarComponent,
    RtTooltip,
    RouterLink,
    BadgeComponent,
    DividerComponent,
    IconColorDirective,
    IconComponent,
    OfferStatusIndicatorComponent,
    OverdueCandidateBadgeComponent,
    TagComponent,
    DateComponent,
    PlaceloaderComponent,
    IntegrationsElementOutletComponent,
    InterviewEvaluationRatingComponent,
    HiddenScoreIconComponent,
    DataTableComponent,
    DaysLeftPipe,
    MomentModule,
    TranslatePipe,
    TitleCasePipe,
  ],
})
export class SearchPageCandidatesTableComponent implements OnInit, OnChanges {
  public readonly EEODataStatus: typeof EEODataStatus = EEODataStatus;
  public readonly OfferKind: typeof OfferKind = OfferKind;
  public readonly DisqualifyKind: typeof DisqualifyKind = DisqualifyKind;

  @Input() public candidates: FullSearchCandidateRecord[] | null;
  @Input() public elementsPerCandidate: { [candidateId: number]: IntegrationsElement[] };
  @Input() public columns: CandidateDataTableColumn[];
  @Input() public allColumns: CandidateDataTableColumn[];
  @Input() public sortBy: SortByType;
  @Input() public autoFit: boolean = true;
  @Input() public selectable: boolean = true;
  @Input() public loaded: boolean = true;
  @Input() public selectedIdsDict: { [key: number]: true } = {};
  @Input() public view: ViewTypeStr;
  @Input() public overdueCandidatesEnabled: boolean;
  @Input() public admins: Record<User['id'], User>;
  @Input() public isLinkWithHref: boolean = true;
  @Input() public limits: CandidateDataTableRowLimits = {
    placements: 4,
  };
  @Input() public expandableCellGroups = ExpandableCellGroups;
  @Input() public offerAccessConfig: OfferAccessConfig = { accessControlEnabled: false };
  @Input() public customCellTemplates: { [view: string]: { [key: string]: TemplateRef<any> } } = {};

  @Output() public candidateSelect: EventEmitter<CandidatesTableRow> = new EventEmitter();
  @Output() public candidateOpen: EventEmitter<CandidatesTableRow> = new EventEmitter();
  @Output() public sortChange: EventEmitter<SortByType> = new EventEmitter();
  @Output()
  public optionsChange: EventEmitter<{
    autoFit: boolean;
    columns: CandidateDataTableColumn[];
  }> = new EventEmitter();
  public trackByValue = trackByValue;

  public CellContext: DataTableCellContext<CandidatesTableRow>;
  public HeaderContext: { col: CandidateDataTableColumn };
  public rows: CandidatesTableRow[] = [];
  public dtColumns: CandidateDataTableColumn[] = [];
  public cellTemplates: { [view: string]: { [key: string]: TemplateRef<any> } };
  public placeloaderRows: DataTableRow[] = Array(5)
    .fill(0)
    .map((x, i) => ({ id: i }));
  public extendedHighlights: { [key: string]: boolean } = {};
  public disqualifyDates: { [key: number]: string } = {};

  public expandedListItems: Record<string, Record<number, boolean>> = {};
  public locations: LocationBrief[] = [];

  public hasNewCandidateProfile = injectDevFlag(DevFlag.CandidateProfileRefresh);

  @ViewChild('expandable', { static: true }) public expandable: TemplateRef<any>;
  @ViewChild('name', { static: true }) private name: TemplateRef<any>;
  @ViewChild('positiveRating', { static: true }) private positiveRating: TemplateRef<any>;
  @ViewChild('jobs', { static: true }) private jobs: TemplateRef<any>;
  @ViewChild('talentPools', { static: true }) private talentPools: TemplateRef<any>;
  @ViewChild('stages', { static: true }) private stages: TemplateRef<any>;
  @ViewChild('jobFitScore', { static: true }) private jobFitScore: TemplateRef<any>;
  @ViewChild('tags', { static: true }) private tags: TemplateRef<any>;
  @ViewChild('sources', { static: true }) private sources: TemplateRef<any>;
  @ViewChild('createdAt', { static: true }) private createdAt: TemplateRef<any>;
  @ViewChild('hiredAt', { static: true }) private hiredAt: TemplateRef<any>;
  @ViewChild('jobStartsAt', { static: true }) private jobStartsAt: TemplateRef<any>;
  @ViewChild('deletedAt', { static: true }) private deletedAt: TemplateRef<any>;
  @ViewChild('gdprExpiresAt', { static: true }) private gdprExpiresAt: TemplateRef<any>;
  @ViewChild('gdprStatus', { static: true }) private gdprStatus: TemplateRef<any>;
  @ViewChild('eeoStatus', { static: true }) private eeoStatus: TemplateRef<any>;
  @ViewChild('gdprUncompletedChangeRequestCreatedAt', { static: true })
  private gdprUncompletedChangeRequestCreatedAt: TemplateRef<any>;
  @ViewChild('gdprUncompletedRemovalRequestCreatedAt', { static: true })
  private gdprUncompletedRemovalRequestCreatedAt: TemplateRef<any>;
  @ViewChild('integrations', { static: true }) private integrations: TemplateRef<any>;
  @ViewChild('lastActivity', { static: true }) private lastActivity: TemplateRef<any>;
  @ViewChild('disqualifyDate', { static: true }) private disqualifyDate: TemplateRef<any>;
  @ViewChild('deletedBy', { static: true }) private deletedBy: TemplateRef<{
    row: CandidatesTableRow;
  }>;
  @ViewChild('disqualifiedBy', { static: true }) private disqualifiedBy: TemplateRef<any>;
  @ViewChild('workLocations', { static: true }) private workLocations: TemplateRef<any>;
  @ViewChild('jobLocationNames', { static: true }) private jobLocationNames: TemplateRef<any>;
  @ViewChild('jobLocationAddress', { static: true }) private jobLocationAddress: TemplateRef<any>;

  @LifeCycle('onDestroy')
  private ngOnDestroy$: Observable<void>;

  constructor(
    private i18n: I18nService,
    private appNavigator: AppNavigatorService,
    private cdRef: ChangeDetectorRef,
    public timeService: TimeService,
    private phoneNumberPipe: PhoneNumberPipe,
    private store: Store,
  ) {
    this.store
      .select(LocationSelectors.allEntities)
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(locations => {
        this.locations = locations;
      });
  }

  // eslint-disable-next-line max-lines-per-function
  public ngOnInit(): void {
    this.cellTemplates = {
      [ViewType.Candidates]: {
        [CandidateColumnId.Name]: this.name,
        [CandidateColumnId.Stages]: this.stages,
        [CandidateColumnId.PositiveRating]: this.positiveRating,
        [CandidateColumnId.Jobs]: this.jobs,
        [CandidateColumnId.JobFitScore]: this.jobFitScore,
        [CandidateColumnId.TalentPools]: this.talentPools,
        [CandidateColumnId.EEOStatus]: this.eeoStatus,
        [CandidateColumnId.Tags]: this.tags,
        [CandidateColumnId.Sources]: this.sources,
        [CandidateColumnId.CreatedAt]: this.createdAt,
        [CandidateColumnId.Integrations]: this.integrations,
        [CandidateColumnId.LastActivity]: this.lastActivity,
        [CandidateColumnId.DisqualifiedBy]: this.disqualifiedBy,
        [CandidateColumnId.DisqualifyDate]: this.disqualifyDate,
        [CandidateColumnId.GdprStatus]: this.gdprStatus,
        [CandidateColumnId.ExpiringIn]: this.gdprExpiresAt,
        [CandidateColumnId.HiredAt]: this.hiredAt,
        [CandidateColumnId.JobStartsAt]: this.jobStartsAt,
        [CandidateColumnId.WorkLocation]: this.workLocations,
        [CandidateColumnId.JobLocationName]: this.jobLocationNames,
        [CandidateColumnId.JobLocationAddress]: this.jobLocationAddress,
        ...(this.customCellTemplates[ViewType.Candidates] ?? {}),
      },
      [ViewType.Hires]: {
        [CandidateColumnId.Name]: this.name,
        [CandidateColumnId.Stages]: this.stages,
        [CandidateColumnId.PositiveRating]: this.positiveRating,
        [CandidateColumnId.Jobs]: this.jobs,
        [CandidateColumnId.JobFitScore]: this.jobFitScore,
        [CandidateColumnId.TalentPools]: this.talentPools,
        [CandidateColumnId.EEOStatus]: this.eeoStatus,
        [CandidateColumnId.Tags]: this.tags,
        [CandidateColumnId.Sources]: this.sources,
        [CandidateColumnId.CreatedAt]: this.createdAt,
        [CandidateColumnId.Integrations]: this.integrations,
        [CandidateColumnId.LastActivity]: this.lastActivity,
        [CandidateColumnId.DisqualifiedBy]: this.disqualifiedBy,
        [CandidateColumnId.DisqualifyDate]: this.disqualifyDate,
        [CandidateColumnId.GdprStatus]: this.gdprStatus,
        [CandidateColumnId.ExpiringIn]: this.gdprExpiresAt,
        [CandidateColumnId.HiredAt]: this.hiredAt,
        [CandidateColumnId.JobStartsAt]: this.jobStartsAt,
        [CandidateColumnId.WorkLocation]: this.workLocations,
        [CandidateColumnId.JobLocationName]: this.jobLocationNames,
        [CandidateColumnId.JobLocationAddress]: this.jobLocationAddress,
      },
      [ViewType.GdprExpiringCandidates]: {
        [CandidateColumnId.Name]: this.name,
        [CandidateColumnId.Jobs]: this.jobs,
        [CandidateColumnId.Stages]: this.stages,
        [CandidateColumnId.CreatedAt]: this.createdAt,
        [CandidateColumnId.HiredAt]: this.hiredAt,
        [CandidateColumnId.JobStartsAt]: this.jobStartsAt,
        [CandidateColumnId.TalentPools]: this.talentPools,
        [CandidateColumnId.GdprStatus]: this.gdprStatus,
        [CandidateColumnId.ExpiringIn]: this.gdprExpiresAt,
      },
      [ViewType.GdprAwaitingConsentCandidates]: {
        [CandidateColumnId.Name]: this.name,
        [CandidateColumnId.Jobs]: this.jobs,
        [CandidateColumnId.Stages]: this.stages,
        [CandidateColumnId.CreatedAt]: this.createdAt,
        [CandidateColumnId.HiredAt]: this.hiredAt,
        [CandidateColumnId.JobStartsAt]: this.jobStartsAt,
        [CandidateColumnId.TalentPools]: this.talentPools,
        [CandidateColumnId.GdprStatus]: this.gdprStatus,
        [CandidateColumnId.ExpiringIn]: this.gdprExpiresAt,
      },
      [ViewType.GdprRemovalCandidates]: {
        [CandidateColumnId.Name]: this.name,
        [CandidateColumnId.Jobs]: this.jobs,
        [CandidateColumnId.TalentPools]: this.talentPools,
        [CandidateColumnId.RequestedDelete]: this.gdprUncompletedRemovalRequestCreatedAt,
        [CandidateColumnId.CreatedAt]: this.createdAt,
        [CandidateColumnId.HiredAt]: this.hiredAt,
        [CandidateColumnId.JobStartsAt]: this.jobStartsAt,
      },
      [ViewType.GdprCorrectionCandidates]: {
        [CandidateColumnId.Name]: this.name,
        [CandidateColumnId.Jobs]: this.jobs,
        [CandidateColumnId.TalentPools]: this.talentPools,
        [CandidateColumnId.RequestedChange]: this.gdprUncompletedChangeRequestCreatedAt,
        [CandidateColumnId.CreatedAt]: this.createdAt,
        [CandidateColumnId.HiredAt]: this.hiredAt,
        [CandidateColumnId.JobStartsAt]: this.jobStartsAt,
      },
      [ViewType.RecentlyDeleted]: {
        [CandidateColumnId.Name]: this.name,
        [CandidateColumnId.Jobs]: this.jobs,
        [CandidateColumnId.DeletedAt]: this.deletedAt,
        [CandidateColumnId.DeletedBy]: this.deletedBy,
      },
      [ViewType.NoEEOData]: {
        [CandidateColumnId.Name]: this.name,
        [CandidateColumnId.PositiveRating]: this.positiveRating,
        [CandidateColumnId.Jobs]: this.jobs,
        [CandidateColumnId.EEOStatus]: this.eeoStatus,
        [CandidateColumnId.TalentPools]: this.talentPools,
        [CandidateColumnId.CreatedAt]: this.createdAt,
        [CandidateColumnId.HiredAt]: this.hiredAt,
        [CandidateColumnId.JobStartsAt]: this.jobStartsAt,
      },
      [ViewType.EEODataRequested]: {
        [CandidateColumnId.Name]: this.name,
        [CandidateColumnId.PositiveRating]: this.positiveRating,
        [CandidateColumnId.Jobs]: this.jobs,
        [CandidateColumnId.EEOStatus]: this.eeoStatus,
        [CandidateColumnId.TalentPools]: this.talentPools,
        [CandidateColumnId.CreatedAt]: this.createdAt,
        [CandidateColumnId.HiredAt]: this.hiredAt,
        [CandidateColumnId.JobStartsAt]: this.jobStartsAt,
      },
    };
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const { candidates, sortBy, columns, admins } = changes;

    if (columns) {
      const columnIds = (this.allColumns ?? this.columns).map(c => c.id);
      this.expandedListItems = {
        ...this.expandedListItems,
        ...columnIds.reduce(
          (acc, c) => (acc[c] ? acc : { ...acc, [c]: {} }),
          this.expandedListItems,
        ),
      };
      const currentCandidates = this.candidates || [];
      const currentAdmins = this.admins || {};
      this.rows = this.buildRowsFromCandidates(currentCandidates, currentAdmins, this.limits || {});
    }

    if (candidates || admins) {
      this.extendedHighlights = {};
      const currentCandidates = this.candidates || [];
      const currentAdmins = this.admins || {};
      this.rows = this.buildRowsFromCandidates(currentCandidates, currentAdmins, this.limits || {});
      this.setDisqualifyDates(this.getDisqualifyDates(currentCandidates));
    }

    if (sortBy || columns) {
      this.dtColumns = this.applySortToColumns();
    }
  }

  public hasAccessToOffer(offerId: number): boolean {
    if (!this.offerAccessConfig.accessControlEnabled) return true;
    return (
      this.offerAccessConfig.hasAccessToAllOffers ||
      !!this.offerAccessConfig.hasAccessToOfferIds?.includes(offerId)
    );
  }

  public hasHighlightExtended(candidateId: number, highlightKey: string): boolean {
    const key = `${candidateId}-${highlightKey}`;
    return this.extendedHighlights[key];
  }

  public transformHighlightEmTags(highlight: string): string {
    return highlight.replace(/<em>/g, '<strong>').replace(/\<\/em\>/g, '</strong>');
  }

  public toggleExtendHighlight(candidateId: number, highlightKey: string): void {
    const key = `${candidateId}-${highlightKey}`;
    this.extendedHighlights[key] = !this.extendedHighlights[key];
  }

  public handleGeometryChange(event: DataTableColumnGeometryChange): void {
    this.optionsChange.emit({
      autoFit: event.autofit,
      columns: this.allColumns.map(col => {
        const modified = event.columns.find(c => c.id === col.id);
        return {
          ...col,
          geometry: modified ? modified.geometry : col.geometry,
        };
      }),
    });
  }

  public handleSortChange(event: CandidateDataTableColumn): void {
    this.sortChange.emit(this.getSortTypeForColumn(event.id, event.sort!));
  }

  public handleRowClick(candidate: CandidatesTableRow): void {
    if (Object.keys(this.selectedIdsDict).length === 0) {
      this.candidateOpen.emit(candidate);
    } else {
      this.candidateSelect.emit(candidate);
    }
  }
  public openCandidateDialog(id: number) {
    this.appNavigator.openCandidateDialog(id);
  }

  public handleRowSelect(event: { row: CandidatesTableRow; selected: boolean }): void {
    this.candidateSelect.emit(event.row);
  }

  public trackByHighlight(index: number, entity: CandidatesTableRowHighlight): number {
    return index;
  }

  public trackByEntityId<T extends { id: number }>(index: number, entity: T): number {
    return entity.id;
  }

  public trackByStage(index: number, entity: CandidatesTableStage) {
    return index;
  }

  public trackByLocationGroup(index: number, entity: LocationBrief[]) {
    return index;
  }

  public trackByLocation(index: number, entity: LocationBrief) {
    return entity.id;
  }

  public isExpired(gdprExpiresAt: string): boolean {
    return moment(gdprExpiresAt).isSameOrBefore(moment());
  }

  public isExpiring(gdprExpiresAt: string): boolean {
    return moment(gdprExpiresAt).diff(moment(), 'd', false) <= 14;
  }

  public isGdprStatusNoConsent(gdprStatus: TGdprStatus) {
    return gdprStatus === GdprStatus.NO_CONSENT;
  }

  public allowOpenInNewWindow($event: MouseEvent): void {
    if ($event.metaKey || $event.which === 2) {
      $event.stopImmediatePropagation();
    }
  }

  public elementsLoading(candidate: FullSearchCandidateRecord): boolean {
    return this.elementsPerCandidate[candidate.id] === null;
  }

  public elementsFor(candidate: FullSearchCandidateRecord): IntegrationsElement[] {
    return (
      this.mapIntegrationElementsForAnonymousCandidate(
        this.elementsPerCandidate[candidate.id],
        candidate,
      ) || []
    );
  }

  public expandList(group: 'placements', id: number) {
    this.toggleCellsList(true, id, ...this.expandableCellGroups[group]);
    this.cdRef.detectChanges();
  }

  public collapseList(group: 'placements', id: number) {
    this.toggleCellsList(false, id, ...this.expandableCellGroups[group]);
    this.cdRef.detectChanges();
  }

  public getDisqualifiedByLabel(placement: CandidatePlacement) {
    const { disqualifyKind, disqualifiedBy, disqualifiedByName } = placement;

    if (disqualifyKind === DisqualifyKind.System)
      return this.i18n.translate('app.search.table.knockout_question');

    if (!disqualifiedBy) return this.i18n.translate('app.search.table.deleted_user');

    if (this.admins[disqualifiedBy])
      return `${this.admins[disqualifiedBy].firstName} ${this.admins[disqualifiedBy].lastName}`;
    else if (disqualifiedByName) return disqualifiedByName;

    return this.i18n.translate('app.search.table.deleted_user');
  }

  public elementsTrackByFn(_index: number, element: IntegrationsElement): string {
    return getElementSignature(element);
  }

  private toggleCellsList(value: boolean, id: number, ...columnIds: string[]) {
    columnIds.forEach(columnId => {
      this.expandedListItems[columnId] = this.expandedListItems[columnId] ?? {};
      this.expandedListItems[columnId][id] = value;
    });
  }

  // eslint-disable-next-line max-lines-per-function
  private getSortTypeForColumn(
    columnId: CandidateColumnId,
    direction: 'desc' | 'asc' = 'asc',
  ): SortByType {
    switch (columnId) {
      case CandidateColumnId.Stages:
        return direction === 'asc' ? SortByType.StageNameDesc : SortByType.StageNameAsc;

      case CandidateColumnId.Jobs:
        return direction === 'asc' ? SortByType.JobTitleDesc : SortByType.JobTitleAsc;

      case CandidateColumnId.TalentPools:
        return direction === 'asc' ? SortByType.TalentPoolTitleDesc : SortByType.TalentPoolTitleAsc;

      case CandidateColumnId.PositiveRating:
        return direction === 'asc' ? SortByType.PositiveRatingsDesc : SortByType.PositiveRatingsAsc;

      case CandidateColumnId.Name:
        return direction === 'asc' ? SortByType.NameDesc : SortByType.NameAsc;

      case CandidateColumnId.CreatedAt:
        return direction === 'asc' ? SortByType.CreatedAtDesc : SortByType.CreatedAtAsc;

      case CandidateColumnId.ExpiringIn:
        return direction === 'asc' ? SortByType.GdprExpiresAtDesc : SortByType.GdprExpiresAtAsc;

      case CandidateColumnId.RequestedDelete:
        // eslint-disable-next-line max-lines
        return direction === 'asc'
          ? SortByType.GdprRemovalRequestCreatedAtDesc
          : SortByType.GdprRemovalRequestCreatedAtAsc;

      case CandidateColumnId.RequestedChange:
        return direction === 'asc'
          ? SortByType.GdprChangeRequestCreatedAtDesc
          : SortByType.GdprChangeRequestCreatedAtAsc;

      case CandidateColumnId.LastActivity:
        return direction === 'asc' ? SortByType.LastActivityDesc : SortByType.LastActivityAsc;

      case CandidateColumnId.DisqualifiedBy:
        return direction === 'asc' ? SortByType.DisqualifiedByDesc : SortByType.DisqualifiedByAsc;

      case CandidateColumnId.DisqualifyDate:
        return direction === 'asc' ? SortByType.DisqualifyDateDesc : SortByType.DisqualifyDateAsc;

      case CandidateColumnId.DeletedBy:
        return direction === 'asc' ? SortByType.DeletedByDesc : SortByType.DeletedByAsc;

      case CandidateColumnId.DeletedAt:
        return direction === 'asc' ? SortByType.DeletedAtDesc : SortByType.DeletedAtAsc;

      case CandidateColumnId.JobFitScore:
        return direction === 'asc' ? SortByType.JobFitScoreDesc : SortByType.JobFitScoreAsc;

      case CandidateColumnId.HiredAt:
        return direction === 'asc' ? SortByType.HiredAtDesc : SortByType.HiredAtAsc;

      case CandidateColumnId.JobStartsAt:
        return direction === 'asc' ? SortByType.JobStartsAtDesc : SortByType.JobStartsAtAsc;

      case CandidateColumnId.WorkLocation:
        return direction === 'asc'
          ? SortByType.PreferredLocationDesc
          : SortByType.PreferredLocationAsc;

      default:
        return SortByType.RelevanceDesc;
    }
  }

  // eslint-disable-next-line max-lines-per-function
  private getTableSortOptions(sort: SortByType): {
    columnId: CandidateColumnId | null;
    direction?: 'asc' | 'desc';
  } {
    switch (sort) {
      case SortByType.StageNameDesc:
        return { columnId: CandidateColumnId.Stages, direction: 'desc' };

      case SortByType.StageNameAsc:
        return { columnId: CandidateColumnId.Stages, direction: 'asc' };

      case SortByType.JobTitleDesc:
        return { columnId: CandidateColumnId.Jobs, direction: 'desc' };

      case SortByType.JobTitleAsc:
        return { columnId: CandidateColumnId.Jobs, direction: 'asc' };

      case SortByType.TalentPoolTitleDesc:
        return { columnId: CandidateColumnId.TalentPools, direction: 'desc' };

      case SortByType.TalentPoolTitleAsc:
        return { columnId: CandidateColumnId.TalentPools, direction: 'asc' };

      case SortByType.PositiveRatingsDesc:
        return { columnId: CandidateColumnId.PositiveRating, direction: 'desc' };

      case SortByType.PositiveRatingsAsc:
        return { columnId: CandidateColumnId.PositiveRating, direction: 'asc' };

      case SortByType.NameDesc:
        return { columnId: CandidateColumnId.Name, direction: 'desc' };

      case SortByType.NameAsc:
        return { columnId: CandidateColumnId.Name, direction: 'asc' };

      case SortByType.CreatedAtDesc:
        return { columnId: CandidateColumnId.CreatedAt, direction: 'desc' };

      case SortByType.CreatedAtAsc:
        return { columnId: CandidateColumnId.CreatedAt, direction: 'asc' };

      case SortByType.GdprExpiresAtAsc:
        return { columnId: CandidateColumnId.ExpiringIn, direction: 'asc' };

      case SortByType.GdprExpiresAtDesc:
        return { columnId: CandidateColumnId.ExpiringIn, direction: 'desc' };

      case SortByType.GdprChangeRequestCreatedAtAsc:
        return { columnId: CandidateColumnId.RequestedChange, direction: 'asc' };

      case SortByType.GdprChangeRequestCreatedAtDesc:
        return { columnId: CandidateColumnId.RequestedChange, direction: 'desc' };

      case SortByType.GdprRemovalRequestCreatedAtAsc:
        return { columnId: CandidateColumnId.RequestedDelete, direction: 'asc' };

      case SortByType.GdprRemovalRequestCreatedAtDesc:
        return { columnId: CandidateColumnId.RequestedDelete, direction: 'desc' };

      case SortByType.LastActivityAsc:
        return { columnId: CandidateColumnId.LastActivity, direction: 'asc' };

      case SortByType.LastActivityDesc:
        return { columnId: CandidateColumnId.LastActivity, direction: 'desc' };

      case SortByType.DisqualifiedByAsc:
        return { columnId: CandidateColumnId.DisqualifiedBy, direction: 'asc' };

      case SortByType.DisqualifiedByDesc:
        return { columnId: CandidateColumnId.DisqualifiedBy, direction: 'desc' };

      case SortByType.DisqualifyDateAsc:
        return { columnId: CandidateColumnId.DisqualifyDate, direction: 'asc' };

      case SortByType.DisqualifyDateDesc:
        return { columnId: CandidateColumnId.DisqualifyDate, direction: 'desc' };

      case SortByType.DeletedAtAsc:
        return { columnId: CandidateColumnId.DeletedAt, direction: 'asc' };

      case SortByType.DeletedAtDesc:
        return { columnId: CandidateColumnId.DeletedAt, direction: 'desc' };

      case SortByType.DeletedByAsc:
        return { columnId: CandidateColumnId.DeletedBy, direction: 'asc' };

      case SortByType.DeletedByDesc:
        return { columnId: CandidateColumnId.DeletedBy, direction: 'desc' };

      case SortByType.JobFitScoreAsc:
        return { columnId: CandidateColumnId.JobFitScore, direction: 'asc' };

      case SortByType.JobFitScoreDesc:
        return { columnId: CandidateColumnId.JobFitScore, direction: 'desc' };

      case SortByType.HiredAtAsc:
        return { columnId: CandidateColumnId.HiredAt, direction: 'asc' };

      case SortByType.HiredAtDesc:
        return { columnId: CandidateColumnId.HiredAt, direction: 'desc' };

      case SortByType.JobStartsAtAsc:
        return { columnId: CandidateColumnId.JobStartsAt, direction: 'asc' };

      case SortByType.JobStartsAtDesc:
        return { columnId: CandidateColumnId.JobStartsAt, direction: 'desc' };

      case SortByType.PreferredLocationAsc:
        return { columnId: CandidateColumnId.WorkLocation, direction: 'asc' };

      case SortByType.PreferredLocationDesc:
        return { columnId: CandidateColumnId.WorkLocation, direction: 'desc' };

      default:
        return { columnId: null };
    }
  }

  // eslint-disable-next-line max-lines-per-function
  private buildRowsFromCandidates(
    candidates: FullSearchCandidateRecord[],
    admins: Record<User['id'], User>,
    limits: CandidateDataTableRowLimits,
  ): CandidatesTableRow[] {
    // eslint-disable-next-line max-lines-per-function
    return candidates.map(candidate => {
      const jobPlacements = candidate.placements.filter(p => p.offer && p.offer.kind === 'job');
      const mappedTalentPools = candidate.placements
        .filter(p => p.offer && p.offer.kind === 'talent_pool')
        .map(p => p.offer!);

      const mappedJobs = jobPlacements.map(p => p.offer!);
      const mappedStages = jobPlacements.map(p => {
        if (p.stage) {
          return {
            name: p.disqualified ? p.disqualifyReason : p.stage.name,
            overdueDiff: p.overdueDiff,
            disqualified: p.disqualified,
            hired: p.isHired,
          };
        }

        return {
          name: p.disqualified
            ? p.disqualifyReason
            : this.i18n.translate('app.search.table.sourced'),
          overdueDiff: p.overdueDiff,
          disqualified: p.disqualified,
        };
      });
      const { mappedWorkLocations, mappedJobLocations, mappedLocationPlaceholders } =
        jobPlacements.reduce(
          (acc, current) => {
            const workLocationIds = current.locationIds || [];
            const workLocations = workLocationIds.map(id => this.locations.find(l => l.id === id)!);
            const jobLocationIds = current.offer!.locationIds || [];
            const jobLocations = jobLocationIds.map(id => this.locations.find(l => l.id === id)!);
            const locationPlaceholders = [
              ...(this.hasColumn(CandidateColumnId.WorkLocation) ? [workLocations] : []),
              ...(this.hasColumn(CandidateColumnId.JobLocationName) ||
              this.hasColumn(CandidateColumnId.JobLocationAddress)
                ? [jobLocations]
                : []),
            ].reduce((acc, current) => (acc.length > current.length ? acc : current), []);

            return {
              mappedWorkLocations: [...acc.mappedWorkLocations, workLocations],
              mappedJobLocations: [...acc.mappedJobLocations, jobLocations],
              mappedLocationPlaceholders: [...acc.mappedLocationPlaceholders, locationPlaceholders],
            };
          },
          { mappedWorkLocations: [], mappedJobLocations: [], mappedLocationPlaceholders: [] },
        );
      const hiddenWorkLocationsLength = mappedWorkLocations
        .slice(limits.placements)
        .reduce((acc, current) => acc + current.length, 0);
      const hiddenJobLocationsLength = mappedJobLocations
        .slice(limits.placements)
        .reduce((acc, current) => acc + current.length, 0);
      const deletedByGDPR = candidate.deletedByName === 'GDPR';

      return {
        ...candidate,
        allJobs: mappedJobs,
        allStages: mappedStages,
        allTalentPools: mappedTalentPools,
        allWorkLocations: mappedWorkLocations,
        allJobLocations: mappedJobLocations,
        hiddenWorkLocationsLength: hiddenWorkLocationsLength,
        hiddenJobLocationsLength: hiddenJobLocationsLength,
        locationPlaceholders: mappedLocationPlaceholders,
        jobLocations: mappedJobLocations.slice(0, limits.placements),
        jobs: mappedJobs.slice(0, limits.placements),
        allJobPlacements: jobPlacements,
        jobPlacements: jobPlacements.slice(0, limits.placements),
        stages: mappedStages.slice(0, limits.placements),
        talentPools: candidate.placements
          .filter(p => p.offer && p.offer.kind === 'talent_pool')
          .map(p => p.offer!),
        workLocations: mappedWorkLocations.slice(0, limits.placements),
        formattedHighlights: candidate.highlight
          ? Object.keys(candidate.highlight)
              .map(
                key =>
                  this.formatRowHighlight(
                    key,
                    candidate.highlight![<keyof FullSearchCandidateRecord['highlight']>key],
                  )!,
              )
              .filter(v => !!v)
          : [],
        deletedByAdmin: admins[candidate.deletedBy!],
        deletedByGDPR: deletedByGDPR,
      };
    });
  }

  private hasColumn(columnId: CandidateColumnId): boolean {
    return !!this.columns.find(column => column.id === columnId);
  }

  // eslint-disable-next-line max-lines-per-function
  private formatRowHighlight(key: string, matches: string[]): CandidatesTableRowHighlight | null {
    let label: string;
    let limit = 3;
    let detailsInTooltip = false;

    switch (key) {
      case 'name':
        return null;

      case 'emails':
        label = this.i18n.translate('app.search.highlight.found_in_emails');
        break;

      case 'phones':
        label = this.i18n.translate('app.search.highlight.found_in_phones');
        break;

      case 'coverLetter':
        label = this.i18n.translate('app.search.highlight.found_in_cover_letter');
        limit = 1;
        detailsInTooltip = true;
        break;

      case 'cvText':
        label = this.i18n.translate('app.search.highlight.found_in_cv_text');
        limit = 1;
        detailsInTooltip = true;
        break;

      case 'tags.name':
        label = this.i18n.translate('app.search.highlight.found_in_tags');
        break;

      case 'sources.name':
        label = this.i18n.translate('app.search.highlight.found_in_sources');
        break;

      case 'placements.job.title':
        label = this.i18n.translate('app.search.highlight.found_in_jobs');
        break;

      case 'placements.talentPool.title':
        label = this.i18n.translate('app.search.highlight.found_in_talent_pools');
        break;

      case 'placements.stage.name':
        label = this.i18n.translate('app.search.highlight.found_in_stages');
        break;

      default:
        if (key.startsWith('candidateFields')) {
          label = this.i18n.translate('app.search.highlight.found_in_candidate_fields');
        } else {
          return null;
        }
    }

    const formattedMatches =
      key === 'phones' ? matches.map(m => this.formatPhoneNumber(m)) : matches;

    return {
      key,
      label,
      detailsInTooltip,
      match: formattedMatches.slice(0, limit).join(', '),
      additionalMatches: formattedMatches.slice(limit),
      allMatches: formattedMatches,
    };
  }

  private formatPhoneNumber(number: string): string {
    const phoneWithoutHtml = getTextContentFromHTML(number);
    return `<em>${this.phoneNumberPipe.transform(phoneWithoutHtml)}</em>`;
  }

  private applySortToColumns(): CandidateDataTableColumn[] {
    const sortedCol = this.getTableSortOptions(this.sortBy);
    return this.columns.map(col => {
      if (col.id === sortedCol.columnId) {
        return {
          ...col,
          sort: sortedCol.direction,
        };
      }
      return col;
    });
  }

  private getDisqualifyDates(candidates: FullSearchCandidateRecord[]): { [key: number]: string } {
    return candidates.reduce((acc, current) => {
      const dates = current.placements
        .filter(placement => !!placement.disqualifiedAt)
        .map(placement => new Date(placement.disqualifiedAt))
        .sort(Helpers.getDateSort(false));
      return {
        ...acc,
        ...(dates.length && {
          [current.id]: dates[0].toISOString(),
        }),
      };
    }, {});
  }

  private setDisqualifyDates(dates: { [key: number]: string }): void {
    this.disqualifyDates = dates;
  }

  private mapIntegrationElementsForAnonymousCandidate(
    elements: IntegrationsElement[],
    candidate: FullSearchCandidateRecord,
  ): IntegrationsElement[] {
    if (!candidate.isAnonymous) return elements;

    return elements.map(element => ({
      ...element,
      inputs: {
        ...element.inputs,
        badge: {
          ...element.inputs.badge,
          disabled: true,
          tooltip: this.i18n.translate('app.candidate.integrations_hidden'),
        },
      },
    }));
  }
}
